import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@akuna/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useCreateHospitalization } from "@/hooks/useHospitalizations";
import { useHospitalizationSectors } from "@/hooks/useHospitalizationSectors";
import { usePets } from "@/hooks/usePets";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const makeSchema = (t: (k: string) => string) =>
  z.object({
    pet_id: z.string().optional(),
    baia_id: z.string().optional(),
    motivo: z.string().optional(),
    diagnostico_inicial: z.string().optional(),
    previsao_alta_at: z.string().optional(),
  });

type FormValues = z.infer<ReturnType<typeof makeSchema>>;

interface NewHospitalizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewHospitalizationDialog({ open, onOpenChange }: NewHospitalizationDialogProps) {
  const { t } = useTranslation("hospitalization");
  const navigate = useNavigate();
  const { data: setores = [] } = useHospitalizationSectors();
  const { data: pets = [] } = usePets();
  const createMutation = useCreateHospitalization();

  const schema = makeSchema(t);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      pet_id: "",
      baia_id: "",
      motivo: "",
      diagnostico_inicial: "",
      previsao_alta_at: "",
    },
  });

  const baias = setores.flatMap((s) =>
    s.baias
      .filter((b) => !b.hospitalization_ativa)
      .map((b) => ({ id: b.id, label: `${s.nome} — ${b.nome}` }))
  );

  async function onSubmit(values: FormValues) {
    try {
      const hospitalization = await createMutation.mutateAsync({
        pet_id: values.pet_id,
        baia_id: values.baia_id || null,
        motivo: values.motivo,
        diagnostico_inicial: values.diagnostico_inicial || null,
        previsao_alta_at: values.previsao_alta_at || null,
      });
      toast.success(t("success.hospitalizationCreated"));
      onOpenChange(false);
      form.reset();
      navigate(`/hospitalization/${hospitalization.id}`);
    } catch {
      toast.error(t("errors.saveFailed"));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("admissao.title")}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="pet_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("admissao.pet")}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("admissao.petPlaceholder")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {(pets as { id: string; name: string }[]).map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="baia_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("admissao.baia")}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("admissao.baiaPlaceholder")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {baias.map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="motivo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("admissao.motivo")}</FormLabel>
                  <FormControl>
                    <Textarea placeholder={t("admissao.motivoPlaceholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="diagnostico_inicial"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("admissao.diagnostico")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("admissao.diagnosticoPlaceholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="previsao_alta_at"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("admissao.previsaoAlta")}</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("admissao.cancelar")}
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {t("admissao.confirmar")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
